<template>
  <div
    class="flex flex-col overflow-hidden"
    :class="webviewStore.isActive ? '' : 'flex-1 min-h-0'"
    :style="rootStyles"
  >
    <div
      v-if="webviewStore.isActive"
      class="w-full min-h-[50px] max-h-[50px] flex items-center justify-between border-gray-200 px-2"
    >
      <ArrowLeftIcon
        class="size-[26px] text-gray-800 cursor-pointer"
        @click="handleBackClick"
      />
      <div class="font-medium text-[16px] leading-[22px] text-gray-800">
        Account Settings
      </div>
      <div class="w-[30px]"></div>
    </div>

    <div
      v-else
      class="tablet-h:hidden px-5 pt-[calc(env(safe-area-inset-top)+6px)]"
    >
      <div class="relative flex h-[44px] items-center">
        <BackButton @click="handleBack" />
        <div
          class="pointer-events-none absolute inset-0 flex items-center justify-center text-[17px] font-medium leading-[22px] text-gray-900"
        >
          Account settings
        </div>
      </div>
    </div>

    <div
      v-if="!webviewStore.isActive"
      class="hidden tablet-h:block w-full max-w-[918px] mx-auto px-3 tablet-v:px-4"
    >
      <div class="font-medium text-[24px] leading-[30px] text-gray-800 my-9">
        Settings
      </div>
    </div>

    <div class="pl-4 min-[918px]:pl-0 mt-4 tablet-h:mt-0">
      <Tabs
        v-model="tab"
        :items="tabs"
        variant="fixed"
        class="min-[918px]:max-w-[886px] mx-auto"
      />
    </div>

    <div
      ref="scrollContainer"
      class="flex-1 min-h-0 overflow-y-auto pt-8 tablet-h:pt-10"
      :class="
        webviewStore.isActive
          ? 'pb-[90px]'
          : 'pb-[calc(env(safe-area-inset-bottom)+24px)] tablet-h:pb-10'
      "
    >
      <div class="max-w-[918px] mx-auto px-4 tablet-v:px-4">
        <RouterView />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Tabs } from '@dacr/library'
import { ArrowLeftIcon } from '@dacr/icons'
import BackButton from '@/shared/components/BackButton.vue'
import { useBillingQueryQuery } from '@/shared/graphql/legacy/generated'
import { NativeActionsEnum, useWebviewStore } from '@/shared/stores/webview'
import { useDevice } from '@/shared/composables/useDevice'

const { refetch: refetchBilling } = useBillingQueryQuery(
  {},
  {
    pollInterval: useWebviewStore().isActive ? 10_000 : undefined,
  }
)

const route = useRoute()
const router = useRouter()
const device = useDevice()
const webviewStore = useWebviewStore()

const scrollContainer = ref<HTMLElement>()

const rootStyles = computed(() =>
  webviewStore.isActive
    ? {
        height: `${device.viewportHeight.value}px`,
        paddingTop: `${webviewStore.topSafeAreaHeight}px`,
      }
    : undefined
)

const tab = ref()
const tabs = [
  {
    label: 'Account',
    value: 'account',
  },
  {
    label: 'Plan & Billing',
    value: 'billing',
  },
  {
    label: 'Security',
    value: 'security',
  },
]

watch(tab, () => {
  scrollContainer.value?.scrollTo(0, 0)
  router.push(`/settings/${tab.value}`)
})

watch(
  route,
  () => {
    if (!route.path) return

    tab.value = route.path.replace('/settings/', '')
  },
  {
    deep: true,
    immediate: true,
  }
)

const handleBack = () => {
  router.push('/home')
}

const handleBackClick = () => {
  webviewStore.postMessage({
    type: NativeActionsEnum.NavigationBack,
  })
}

onMounted(() => {
  refetchBilling()
})
</script>
